import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SystemNotification } from "../types/notifications.types";
import { BirthdayNotification } from "../types/loyalty.types";
import {
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotificationById,
} from "../api/notifications.api";
import {
  useQueriedNotifications,
  useUnreadNotificationCount,
  invalidateAllNotificationQueries,
} from "../hooks/useQueriedNotifications";

interface NotificationContextType {
  // Notifications State
  notifications: SystemNotification[];
  birthDayNotifications: BirthdayNotification[]; // Keep for backward compatibility
  unreadCount: number;

  // Actions
  addNotification: (
    notification: SystemNotification | BirthdayNotification,
  ) => void;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAsUnread: (notificationId: string) => void;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;

  // Load notifications from localStorage/backend
  loadNotifications: () => Promise<void>;
  loadUnreadCount: () => Promise<void>;

  // Batch operations
  addBulkNotifications: (
    notifications: (SystemNotification | BirthdayNotification)[],
  ) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

const LOCAL_STORAGE_KEY = "birthday_notifications_legacy";
const DELETED_BIRTHDAY_IDS_KEY = "birthday_notifications_deleted_ids";

// Helper: get the set of permanently-deleted birthday notification IDs
const getDeletedBirthdayIds = (): Set<string> => {
  try {
    const raw = localStorage.getItem(DELETED_BIRTHDAY_IDS_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch {}
  return new Set();
};

// Helper: persist the deleted IDs set
const saveDeletedBirthdayIds = (ids: Set<string>) => {
  localStorage.setItem(DELETED_BIRTHDAY_IDS_KEY, JSON.stringify([...ids]));
};

interface NotificationProviderProps {
  children: ReactNode;
}

/**
 * Check if a JWT token in localStorage is locally non-expired.
 * This is a client-side check only — it does NOT call the server.
 * Returns false if no token, token is malformed, or token is past its exp.
 */
const isTokenLocallyValid = (): boolean => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("authToken");
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload.exp) return true; // No expiry claim — treat as valid
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

/**
 * Check if the user is a super admin.
 * Super admin users don't need notifications - they manage the platform, not customers.
 * Returns true if user has isSuperAdmin permission set to true.
 */
const isSuperAdminUser = (): boolean => {
  try {
    const permissions = localStorage.getItem("permissions");
    if (permissions) {
      const parsed = JSON.parse(permissions);
      return parsed?.isSuperAdmin === true;
    }
  } catch {
    // If permissions can't be parsed, assume not super admin
  }
  return false;
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<SystemNotification[]>([]);
  const [birthDayNotifications, setBirthDayNotifications] = useState<
    BirthdayNotification[]
  >([]);

  // Get QueryClient for cache invalidation on logout
  const queryClient = useQueryClient();

  // Determine if the user is authenticated with a locally-valid token.
  // This is evaluated once on mount — queries are NOT reactive to token changes
  // (they're controlled via enabled flag + manual refetch/invalidation).
  const isAuthenticated = isTokenLocallyValid();

  // Check if user is a super admin - they don't need notifications
  // Recompute on each render to detect permission changes
  const isSuperAdmin = isSuperAdminUser();

  // Notifications should only be enabled for authenticated, non-admin users
  // Super admin users manage the platform and don't need customer notifications
  const shouldFetchNotifications = isAuthenticated && !isSuperAdmin;

  // Use TanStack Query for notifications.
  // Auto-fetching disabled globally. We fetch ONLY when explicitly requested (e.g. Panel Open)
  const { data: queriedNotificationsResponse, refetch: refetchNotifications } =
    useQueriedNotifications({
      limit: 50,
      enabled: false,
    });

  // Use TanStack Query for unread count — auto-fetch disabled
  // We trigger it only from components like NotificationBell
  const { data: unreadCountResponse, refetch: refetchUnreadCount } =
    useUnreadNotificationCount(false);

  // Monitor permission changes and token removal - clear notifications if needed
  useEffect(() => {
    const handleStorageChange = () => {
      const isSuperAdminNow = isSuperAdminUser();
      const token = localStorage.getItem("token");
      const shouldClear = !token || isSuperAdminNow;

      // If user logged out or became super admin, clear notifications
      if (
        shouldClear &&
        (notifications.length > 0 || birthDayNotifications.length > 0)
      ) {
        setNotifications([]);
        setBirthDayNotifications([]);
        invalidateAllNotificationQueries(queryClient);
      }
    };

    // Listen for storage changes (handles logout/permission changes from other tabs)
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [queryClient, notifications.length, birthDayNotifications.length]);

  // Monitor token removal in the same tab (for immediate cleanup on logout)
  useEffect(() => {
    const token = localStorage.getItem("token");
    const isSuperAdminNow = isSuperAdminUser();

    if (
      (!token || isSuperAdminNow) &&
      (notifications.length > 0 || birthDayNotifications.length > 0)
    ) {
      setNotifications([]);
      setBirthDayNotifications([]);
      invalidateAllNotificationQueries(queryClient);
      const reason = !token
        ? "user is not authenticated"
        : "user is super admin";
    }
  }, [
    queryClient,
    isSuperAdmin,
    notifications.length,
    birthDayNotifications.length,
  ]);

  // Sync queried data to local state
  useEffect(() => {
    if (
      queriedNotificationsResponse?.success &&
      queriedNotificationsResponse?.data?.notifications
    ) {
      setNotifications(queriedNotificationsResponse.data.notifications);
    }
  }, [queriedNotificationsResponse]);

  const loadNotifications = useCallback(async () => {
    // Guard: only fetch from backend if the user has a locally-valid token
    // AND is not a super admin. Super admin users don't need notifications.
    // This prevents notification API calls from firing for visitors,
    // unauthenticated users, and especially super admin users managing the platform.
    if (!isTokenLocallyValid() || isSuperAdminUser()) {
      return;
    }

    try {
      // 1. Refresh System Notifications and Unread Count (TanStack query)
      await Promise.all([refetchNotifications(), refetchUnreadCount()]);

      // 2. Load Legacy Birthdays from localStorage
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setBirthDayNotifications(parsed);
        }
      }
    } catch (error) {
      console.error("Failed to fetch or load notifications:", error);
    }
  }, [refetchNotifications, refetchUnreadCount]);

  const loadUnreadCount = useCallback(async () => {
    // Guard: only fetch if there is a valid authenticated session
    // AND the user is not a super admin
    if (!isTokenLocallyValid() || isSuperAdminUser()) {
      return;
    }
    try {
      // Fetch both so local computation handles correctly the latest list
      await Promise.all([refetchNotifications(), refetchUnreadCount()]);
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  }, [refetchUnreadCount]);

  // Save legacy notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify(birthDayNotifications),
    );
  }, [birthDayNotifications]);

  const addNotification = useCallback(
    (notification: SystemNotification | BirthdayNotification) => {
      // Check if it's a legacy birthday notification
      if ("customerId" in notification) {
        setBirthDayNotifications((prev) => {
          const exists = prev.some((n) => n.id === notification.id);
          if (exists) return prev;
          return [notification as BirthdayNotification, ...prev];
        });
      } else {
        // It's a system notification
        setNotifications((prev) => {
          const exists = prev.some((n) => n.id === notification.id);
          if (exists) return prev;
          return [notification as SystemNotification, ...prev];
        });
      }
    },
    [],
  );

  // TanStack Query handles polling automatically via refetchInterval
  // No custom polling hook needed - useQueriedNotifications handles:
  // - Auto-polling every 15 seconds
  // - Window focus refetching
  // - Deduplication & caching
  // - Memory management

  const addBulkNotifications = useCallback(
    (newNotifs: (SystemNotification | BirthdayNotification)[]) => {
      // Get the set of permanently-deleted birthday IDs so we never re-add them
      const deletedIds = getDeletedBirthdayIds();

      const legacyToProcess = newNotifs.filter(
        (n) => "customerId" in n && !deletedIds.has(n.id),
      ) as BirthdayNotification[];
      const systemToProcess = newNotifs.filter(
        (n) => !("customerId" in n),
      ) as SystemNotification[];

      if (legacyToProcess.length > 0) {
        setBirthDayNotifications((prev) => {
          const existingIds = new Set(prev.map((n) => n.id));
          const filtered = legacyToProcess.filter(
            (n) => !existingIds.has(n.id),
          );
          return [...filtered, ...prev];
        });
      }

      if (systemToProcess.length > 0) {
        setNotifications((prev) => {
          const existingIds = new Set(prev.map((n) => n.id));
          const filtered = systemToProcess.filter(
            (n) => !existingIds.has(n.id),
          );
          return [...filtered, ...prev];
        });
      }
    },
    [],
  );

  const markAsRead = useCallback(
    async (notificationId: string) => {
      // Check if legacy
      const isLegacy = birthDayNotifications.some(
        (n) => n.id === notificationId,
      );

      if (isLegacy) {
        setBirthDayNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, isRead: true } : n,
          ),
        );
      } else {
        // System Notification - update UI optimistically
        setNotifications((prev) =>
          prev.map((n) => {
            if (n.id === notificationId && !n.isRead) {
              return { ...n, isRead: true };
            }
            return n;
          }),
        );

        // Call backend asynchronously
        try {
          if (localStorage.getItem("token")) {
            await markNotificationAsRead(notificationId);
          }
        } catch (err) {
          console.error("Failed to mark read in DB", err);
        }
      }
    },
    [birthDayNotifications],
  );

  const markAsUnread = useCallback((notificationId: string) => {
    // Only implemented locally for legacy
    setBirthDayNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, isRead: false } : n)),
    );
    setNotifications((prev) =>
      prev.map((n) => {
        if (n.id === notificationId && n.isRead) {
          return { ...n, isRead: false };
        }
        return n;
      }),
    );
  }, []);

  const markAllAsRead = useCallback(async () => {
    setBirthDayNotifications((prev) =>
      prev.map((n) => ({ ...n, isRead: true })),
    );

    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));

    try {
      if (localStorage.getItem("token")) {
        await markAllNotificationsAsRead();
      }
    } catch (err) {
      console.error("Failed to mark all as read in DB", err);
    }
  }, []);

  const deleteNotification = useCallback(
    async (notificationId: string) => {
      let isReadLegacy = false;
      const isLegacy = birthDayNotifications.some((n) => {
        if (n.id === notificationId) {
          isReadLegacy = n.isRead;
          return true;
        }
        return false;
      });

      if (isLegacy) {
        // If it was already READ, save the deleted ID permanently so it never re-appears
        // If UNREAD, it will disappear now but come back on refresh!
        if (isReadLegacy) {
          const deletedIds = getDeletedBirthdayIds();
          deletedIds.add(notificationId);
          saveDeletedBirthdayIds(deletedIds);
        }

        setBirthDayNotifications((prev) =>
          prev.filter((n) => n.id !== notificationId),
        );
      } else {
        // Optimistic delete
        let wasUnread = false;
        let isSystemRead = false;
        setNotifications((prev) => {
          const target = prev.find((n) => n.id === notificationId);
          if (target && !target.isRead) {
            wasUnread = true;
          } else if (target && target.isRead) {
            isSystemRead = true;
          }
          return prev.filter((n) => n.id !== notificationId);
        });

        try {
          if (localStorage.getItem("token") && isSystemRead) {
            // ONLY delete from backend if it is already READ!
            await deleteNotificationById(notificationId);
          }
        } catch (err) {
          console.error("Failed to delete notification in DB", err);
        }
      }
    },
    [birthDayNotifications],
  );

  const clearAllNotifications = useCallback(async () => {
    // Only permanently delete READ notifications
    const deletedIds = getDeletedBirthdayIds();
    birthDayNotifications.forEach((n) => {
      if (n.isRead) deletedIds.add(n.id);
    });
    saveDeletedBirthdayIds(deletedIds);

    // Keep track of read system notifications to delete individually
    const readIds = notifications.filter((n) => n.isRead).map((n) => n.id);

    setBirthDayNotifications([]);
    setNotifications([]);

    try {
      if (localStorage.getItem("token")) {
        // Sequentially delete ONLY the read notifications instead of wiping everything
        // to preserve the "clear unread -> reappears on refresh" requirement
        for (const id of readIds) {
          await deleteNotificationById(id);
        }
      }
    } catch (err) {
      console.error("Failed to clear read notifications in DB", err);
    }
  }, [birthDayNotifications, notifications]);

  // Calculate unread count strictly for notifications within the last 24 hours
  // This matches the visual filtering logic in NotificationPanel so the count
  // won't include old notifications that are hidden from the user.
  const now = new Date().getTime();

  const activeSystemNotifications = notifications.filter((n) => {
    const created = new Date(n.createdAt).getTime();
    return now - created <= 24 * 60 * 60 * 1000;
  });

  const activeBirthdayNotifications = birthDayNotifications.filter((n) => {
    const created = new Date(n.createdAt).getTime();
    return now - created <= 24 * 60 * 60 * 1000;
  });

  const unreadSystemCount = activeSystemNotifications.filter(
    (n) => !n.isRead,
  ).length;
  const unreadLegacyCount = activeBirthdayNotifications.filter(
    (n) => !n.isRead,
  ).length;

  const unreadCount = unreadSystemCount + unreadLegacyCount;

  const value: NotificationContextType = {
    notifications,
    birthDayNotifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    markAsUnread,
    deleteNotification,
    clearAllNotifications,
    loadNotifications,
    loadUnreadCount,
    addBulkNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationCenter = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotificationCenter must be used within NotificationProvider",
    );
  }
  return context;
};
